import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IntegrationsManageUsersTableRow } from '../../../../admin/components/integrations/IntegrationsManageUsersTableRow';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { invalidateAccountIntegrationQuery, useUserMapMutation } from '../../../../queries';
import { useAccountIntegrationQuery } from '../../../../queries/Account';
import { ResourceAutoCompleteViewModel } from '../../../../viewmodels/AppViewModels';
import { AutoCompleteSearchFieldAccessoryViewMode } from '../../../components/autocomplete/AutoCompleteSearchField';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { DeprecatedSelect, ISelectOption } from '../../../components/DeprecatedSelect';
import { isSpecificOrganizationsManageAms360Enabled } from '../../../components/integrations/utils/isSpecificOrganizationsManageAms360';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const ALL_KEY = 'All';

function _Ams360({ styles: propStyles }: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('Ams360UserMapping');

	const canManage = React.useMemo(() => isSpecificOrganizationsManageAms360Enabled(userSession), [userSession]);

	const accountId = userSession.account.id;
	const impersonationContext = userSession.impersonationContext;
	const integrationType = Api.ConfigurableIntegrationType.Ams360;

	const accountIntegrationQuery = useAccountIntegrationQuery({
		impersonationContext,
		meta: {
			errorLogging: {
				category: 'Ams360UserMapping',
				action: 'AccountIntegrationQuery-Error',
			},
		},
		type: integrationType,
	});

	const integration = accountIntegrationQuery.data as Api.IAms360Integration | undefined;
	const isLoadingIntegration = accountIntegrationQuery.isPending;

	const userMapMutation = useUserMapMutation({
		impersonationContext,
		onError: error => {
			logApiError('AssignMapping-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			invalidateAccountIntegrationQuery({ impersonationContext, type: integrationType });
		},
	});

	const [isEditingTable, setIsEditingTable] = React.useState(false);
	const [filteredConnectionType, setFilteredConnectionType] = React.useState<ISelectOption<string> | null>(null);
	const [pendingUserMap, setPendingUserMap] = React.useState<Record<string, string>>({});
	const hasPendingMappingChanges = Object.keys(pendingUserMap).length > 0;

	const connectedLevIds = React.useMemo(
		() =>
			integration?.integrationUserConfigurations
				? integration.integrationUserConfigurations.filter(conf => conf.isConnected).map(conf => conf.levitateUserId)
				: [],
		[integration?.integrationUserConfigurations]
	);

	// const onEditTable = () => {
	// 	setIsEditingTable(true);
	// };

	const addToPendingUserMap = (user: Api.IIntegrationUserMapping, levitateUserId: string | null) => {
		// If the user is already mapped to the same Levitate user, remove it from the pending map
		let newPendingUserMap: Record<string, string>;
		if ((levitateUserId == null && user.levitateUser?.id == null) || user.levitateUser?.id === levitateUserId) {
			const { [user.integrationId]: _, ...rest } = pendingUserMap;
			newPendingUserMap = rest;
		} else {
			newPendingUserMap = { ...pendingUserMap, [user.integrationId]: levitateUserId };
		}
		setPendingUserMap(newPendingUserMap);
	};

	const bulkSave = () => {
		const userMap = integration?.userMap?.slice() ?? [];
		const pendingIntegrationIds = new Set(Object.keys(pendingUserMap));
		for (const userMapEntry of userMap) {
			if (pendingIntegrationIds.has(userMapEntry.integrationId)) {
				const pendingUserMapEntry = pendingUserMap[userMapEntry.integrationId];
				userMapEntry.levitateUser = pendingUserMapEntry != null ? { id: pendingUserMapEntry } : null;
			}
		}
		userMapMutation.mutate({ type: integrationType, userMap });
		setPendingUserMap({});
		setIsEditingTable(false);
	};

	const onEditTableCancel = () => {
		setPendingUserMap({});
		setIsEditingTable(false);
	};

	const onCreateAutoCompleteViewModel = (
		type: Api.ResourceAutoCompleteViewModelType,
		_suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		// Create a new view model with increased debounce delay to reduce API requests
		// We recreate the view model with the same configuration but higher inputDelay
		const debouncedViewModel = new ResourceAutoCompleteViewModel(userSession, {
			inputDelay: 300, // 300ms debounce delay (default is 100ms)
			pageSize: 5, // Default pageSize (same as SimpleAutoCompleteSearchField default)
			type,
		});
		debouncedViewModel.addParam({ includeDeactivated: false });
		return debouncedViewModel.impersonate(impersonationContext);
	};

	const onUserSelected =
		(user: Api.IIntegrationUserMapping) => (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>) => {
			if (e.target) {
				e.target.setSearchQuery(getDisplayName(e.selection));
			}
			const levUser = new Api.UserViewModel(userSession, e.selection);
			addToPendingUserMap(user, levUser.id);
		};

	const uniqueConnectionTypes: string[] = React.useMemo(() => {
		if (!integration?.userMap) {
			return [ALL_KEY];
		}

		const allConnectionTypeArrays = integration.userMap.map(user => {
			return user.connectionTypes;
		});
		const flattenedConnectionTypes: string[] = allConnectionTypeArrays.flat();
		const distinctConnectionTypes = flattenedConnectionTypes.filter(
			(value, index, array) => array.indexOf(value) === index
		);

		return [ALL_KEY, ...distinctConnectionTypes];
	}, [integration]);

	if (isLoadingIntegration) {
		return <LoadingSpinner type='large' />;
	}

	const getOwnershipRole = () => {
		if (!integration) {
			return 'Unknown';
		}
		const ams = integration as Api.IAms360Integration;
		return ams.ownershipRole?.toString() || 'Unknown';
	};

	const hasOwnershipLock = Boolean(integration?.locks?.ownershipRoleLock);

	const onConnectionTypeFilterChanged = (option: ISelectOption<string>) => {
		if (option.id === ALL_KEY) {
			setFilteredConnectionType(null);
			return;
		}
		setFilteredConnectionType(option);
	};

	const connectionTypeOptions = uniqueConnectionTypes.map(connectionTypeName => {
		return {
			dataContext: connectionTypeName,
			id: connectionTypeName,
			text: connectionTypeName,
		};
	});

	if (!canManage) {
		return <div>You are not authorized to manage the Ams360 integration settings. Please contact support.</div>;
	}

	return (
		<div className={css(styleSheet.root, ...(propStyles || []))}>
			<h1>Configure Ams360 Integration</h1>
			<div className={css(styleSheet.actionToolbar)}>
				<div>Mapping By: {getOwnershipRole()}</div>

				<div className={css(styleSheet.actions)}>
					<span className={css(styleSheet.dropdownContainer)}>
						<div className={css(styleSheet.dropdownText)}>Browse Type:</div>
						<DeprecatedSelect
							maxOptionsToShow={99}
							onOptionClick={onConnectionTypeFilterChanged}
							options={connectionTypeOptions}
							selectedOptionTitle={filteredConnectionType ? filteredConnectionType.text : ALL_KEY}
						/>
					</span>

					{
						isEditingTable ? (
							<>
								<button
									className={css(baseStyleSheet.ctaButtonSmall)}
									disabled={!hasPendingMappingChanges}
									onClick={bulkSave}
								>
									<span>Save</span>
								</button>
								<button className={css(baseStyleSheet.ctaButtonReverseSmall)} onClick={onEditTableCancel}>
									<span>Cancel</span>
								</button>
							</>
						) : null
						// <>
						// COMMENTED OUT WAITING FOR THE INTEGRATION TEAM TO ALIGN ON HOW THIS IS TO BE DONE ONCE THEY HAVE A SOLUTION
						// 	<button
						// 		className={`${css(styleSheet.tableHeaderEditHeaderEditButton)}`}
						// 		disabled={hasOwnershipLock}
						// 		onClick={onEditTable}
						// 	>
						// 		<EditItemPenIcon className={css(styleSheet.tableHeaderEditHeaderEditButtonIcon)} />
						// 		<span>Edit</span>
						// 	</button>
						// </>
					}
				</div>
			</div>
			<div className={css(styleSheet.tableContainer, hasOwnershipLock && styleSheet.tableDisabled)}>
				<table className={css(styleSheet.container)}>
					<thead className={css(styleSheet.tableHeader)}>
						<tr className={css(styleSheet.tableHeaderRow)}>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>Initials</th>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderEmailHeader)}>Type</th>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderEmailHeader)}>Levitate User</th>
							{Api.isMultiUserIntegration(integrationType) ? (
								<>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderStatusIndicatorHeader)} />
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderStatusHeader)}>Status</th>
								</>
							) : null}
						</tr>
					</thead>
					<tbody>
						{(integration?.userMap ?? []).map(x => {
							if (!x.integrationId) {
								return null;
							}
							if (filteredConnectionType && x.connectionTypes?.indexOf(filteredConnectionType.text) === -1) {
								return null;
							}
							const connectionTypes = x?.connectionTypes ? x?.connectionTypes.sort((a, b) => a.localeCompare(b)) : null;
							return (
								<IntegrationsManageUsersTableRow
									accountId={accountId}
									connectedLevIds={connectedLevIds}
									key={x.integrationId}
									integrationUser={x}
									showConnectionStatusColumns={Api.isMultiUserIntegration(integrationType)}
								>
									<td className={css(styleSheet.cell)}>{connectionTypes?.join(', ')}</td>
									<td className={css(styleSheet.cell)}>
										{isEditingTable ? (
											<SimpleAutoCompleteSearchField
												key={x.integrationId}
												dropdownContentItemsStyles={[styleSheet.autoCompleteDropdownItems]}
												dropdownContentStyle={styleSheet.autoCompleteDropdownContent}
												onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
												// @ts-ignore
												onItemSelected={onUserSelected(x)}
												resultsLimit={5}
												pageSize={5}
												placeholder='Search name'
												style={styleSheet.emailInput}
												type={Api.ResourceAutoCompleteViewModelType.User}
												rightAccessoryViewMode={AutoCompleteSearchFieldAccessoryViewMode.Always}
												onClear={() => addToPendingUserMap(x, null)}
												initialSearchQuery={x.levitateUser?.id ? getDisplayName(x.levitateUser) : ''}
											/>
										) : x.levitateUser?.id ? (
											getDisplayName(x.levitateUser)
										) : null}
									</td>
								</IntegrationsManageUsersTableRow>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export const Ams360 = observer(_Ams360);
